/**
 * One setting: label and help on the left, one control on the right, a hairline below. Changes apply at once.
 * `for`: id of the control, so the label is a real <label>. Without it, pass children as a function
 * ({ labelId, helpId }) => control and wire aria-labelledby / aria-describedby yourself (for groups).
 * `inline`: keep the control on the right on phones (checkboxes, switches). Otherwise it stacks full width.
 * `danger`: label in the danger colour (Delete account).
 * `ids`: a prefix that names the label and help (`<ids>-label`, `<ids>-help`) instead of generated ids, for a page whose
 * controls and tests point at them (PLAN.md §3, step 1).
 * (cockatrice-ui `src/ui/SettingsRow.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/settings-row.css';
import { createUniqueId, mergeProps, splitProps } from 'solid-js';
import { el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface SettingsRowIds {
  /** Id of the label element; use for aria-labelledby. */
  readonly labelId: string;
  /** Id of the help text, when there is help; use for aria-describedby. */
  readonly helpId?: string | undefined;
}
export interface SettingsRowProps extends Attributes {
  /** Setting name. */
  readonly label: Child;
  /** Id of the control; makes the label a real <label>. */
  readonly for?: string | undefined;
  /** One line of help. */
  readonly help?: Child;
  /** Current state, in a caption line. */
  readonly status?: Child;
  /** The control, or a function that receives ids for grouped controls. */
  readonly children?: Child | ((ids: SettingsRowIds) => Child);
  /** Keep the control on the right on phones (checkbox, switch). */
  readonly inline?: boolean | undefined;
  /** Label in the danger colour. */
  readonly danger?: boolean | undefined;
  /** Hairline below. Default true. */
  readonly divided?: boolean | undefined;
  /** Names the label and help `<ids>-label` and `<ids>-help` (otherwise generated ids). */
  readonly ids?: string | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function SettingsRow(props: SettingsRowProps): Child {
  const [local, rest] = splitProps(props, ['class', 'for', 'label', 'help', 'children', 'inline', 'danger', 'divided', 'status', 'ids']);
  const uid = local.ids ?? createUniqueId();
  const labelId = `${uid}-label`;
  const helpId = `${uid}-help`;
  const control = (): Child => {
    const children = local.children;
    return typeof children === 'function' ? children({ labelId, helpId: local.help ? helpId : undefined }) : children;
  };
  return el(
    'div',
    mergeProps(
      {
        class: () =>
          [
            'ui-settings-row',
            local.inline === true && 'ui-settings-row--inline',
            local.danger === true && 'ui-settings-row--danger',
            local.divided === false && 'ui-settings-row--plain',
            local.class,
          ]
            .filter(Boolean)
            .join(' '),
      },
      rest,
    ),
    el(
      'div',
      { class: 'ui-settings-row__text' },
      when(
        () => local.for !== undefined && local.for !== '',
        () => el('label', { class: 'ui-settings-row__label', id: labelId, for: () => local.for }, () => local.label),
        () => el('div', { class: 'ui-settings-row__label', id: labelId }, () => local.label),
      ),
      when(
        () => Boolean(local.help),
        () => el('div', { class: 'ui-settings-row__help', id: helpId }, () => local.help),
      ),
      when(
        () => Boolean(local.status),
        () => el('div', { class: 'ui-settings-row__status' }, () => local.status),
      ),
    ),
    el('div', { class: 'ui-settings-row__control' }, control),
  );
}
